import { useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import {
  NoToneMapping,
  RenderPipeline,
  SRGBColorSpace,
  Vector2,
  type Camera,
  type Scene,
  type WebGPURenderer,
} from 'three/webgpu';
import { float, mrt, nodeObject, normalView, output, pass, uniform, uv, vec2, vec4 } from 'three/tsl';
import type { Node } from 'three/webgpu';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { chromaticAberration } from 'three/addons/tsl/display/ChromaticAberrationNode.js';
import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { film } from 'three/addons/tsl/display/FilmNode.js';
import { smaa } from 'three/addons/tsl/display/SMAANode.js';

import { BLOOM_THRESHOLD } from '../core/constants';
import { autoLevels } from './autoLevels';

/**
 * The `three/addons` effect nodes are declared as bare `TempNode`s, which drops
 * the fluent node methods. They all produce colour, so this restores the type
 * the graph actually carries.
 */
const asColour = (node: unknown) => nodeObject(node as Node<'vec4'>);

export interface EffectSettings {
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
  aoRadius: number;
  aoScale: number;
  focusDistance: number;
  focalLength: number;
  bokehScale: number;
  aberration: number;
  grain: number;
  vignette: number;
  exposure: number;
  autoLevels: number;
}

export const DEFAULT_EFFECTS: EffectSettings = {
  bloomStrength: 0.8,
  bloomRadius: 0.6,
  bloomThreshold: BLOOM_THRESHOLD,
  aoRadius: 0.35,
  aoScale: 1.2,
  // Distances are in world units: the camera sits 6 units back from the
  // instrument, and the falloff is wide so only the set piece behind it softens.
  focusDistance: 6,
  focalLength: 7,
  bokehScale: 1.6,
  aberration: 0.02,
  grain: 0.04,
  vignette: 0.7,
  exposure: 1.3,
  // Full stretch. The frame normally spans the range already, so this mostly
  // idles and only earns its keep when the scene darkens.
  autoLevels: 1,
};

/**
 * The full-screen effect chain, written as a TSL node graph.
 *
 * Order matters: ambient occlusion and bloom are HDR operations and run on the
 * linear scene colour, `renderOutput` then applies tone mapping and the output
 * colour transform, and the lens artefacts (aberration, grain, vignette) and
 * antialiasing run last, on display-referred colour, so they behave the way
 * they would on a real camera. Auto-levels closes the chain, measuring the
 * finished frame and stretching it back out to fill the display range.
 */
export function Effects({ settings = DEFAULT_EFFECTS }: { settings?: EffectSettings }) {
  const renderer = useThree((state) => state.gl) as unknown as WebGPURenderer;
  const scene = useThree((state) => state.scene) as Scene;
  const camera = useThree((state) => state.camera) as Camera;

  const { post, levels, uniforms } = useMemo(() => {
    const scenePass = pass(scene, camera);
    scenePass.setMRT(mrt({ output, normal: normalView }));

    const colour = scenePass.getTextureNode('output');
    const normal = scenePass.getTextureNode('normal');
    const depth = scenePass.getTextureNode('depth');
    const viewZ = scenePass.getViewZNode();

    // Contact shadows in the instrument's crevices and under the set piece.
    // The AO target is single-channel, so only its red channel is meaningful —
    // multiplying by the whole sample would zero green and blue.
    const occlusion = ao(depth, normal, camera);
    occlusion.resolutionScale = 0.5;
    const occluded = colour.mul(nodeObject(occlusion.getTextureNode()).r);

    // A shallow rack focus keeps attention on the panel.
    const focusDistance = uniform(settings.focusDistance);
    const focalLength = uniform(settings.focalLength);
    const bokehScale = uniform(settings.bokehScale);
    const focused = asColour(dof(occluded, viewZ, focusDistance, focalLength, bokehScale));

    const bloomPass = bloom(focused, settings.bloomStrength, settings.bloomRadius, settings.bloomThreshold);
    const glow = asColour(bloomPass);

    const exposure = uniform(settings.exposure);
    // No tone curve, just the sRGB transfer — the same thing the original's
    // `gammaOutput` did. A filmic curve rolls off exactly the self-lit panel
    // highlights the instrument is meant to be read by, so anything over white
    // is left to clip and bloom instead. Naming the transform here rather than
    // reading `renderer.toneMapping` keeps the graph independent of when R3F
    // applies its own colour settings.
    const graded = asColour(
      focused.mul(exposure).add(glow).renderOutput(NoToneMapping, SRGBColorSpace),
    );

    const aberration = uniform(settings.aberration);
    const fringed = asColour(
      chromaticAberration(graded, aberration, new Vector2(0.5, 0.5), float(1.04)),
    );

    const grain = uniform(settings.grain);
    const grained = asColour(film(fringed, grain));

    // Radial falloff, squared for a soft shoulder rather than a hard ring.
    const vignetteAmount = uniform(settings.vignette);
    const distance = uv().sub(vec2(0.5, 0.5)).length().mul(1.414);
    const vignette = distance.mul(vignetteAmount).oneMinus().clamp(0, 1).pow(1.6);

    const composed = vec4(grained.rgb.mul(vignette), grained.a);

    const antialiased = smaa(composed);
    const levels = autoLevels(
      asColour(antialiased),
      antialiased.getTextureNode().value,
      settings.autoLevels,
    );

    const post = new RenderPipeline(renderer);
    post.outputColorTransform = false;
    post.outputNode = levels.node;

    occlusion.radius.value = settings.aoRadius;
    occlusion.scale.value = settings.aoScale;

    return {
      post,
      levels,
      uniforms: {
        occlusion,
        bloomPass,
        focusDistance,
        focalLength,
        bokehScale,
        exposure,
        aberration,
        grain,
        vignetteAmount,
        levels: levels.strength,
      },
    };
  }, [renderer, scene, camera]);

  // Keep the graph in step with prop changes without rebuilding it.
  useEffect(() => {
    uniforms.occlusion.radius.value = settings.aoRadius;
    uniforms.occlusion.scale.value = settings.aoScale;
    uniforms.bloomPass.strength.value = settings.bloomStrength;
    uniforms.bloomPass.radius.value = settings.bloomRadius;
    uniforms.bloomPass.threshold.value = settings.bloomThreshold;
    uniforms.focusDistance.value = settings.focusDistance;
    uniforms.focalLength.value = settings.focalLength;
    uniforms.bokehScale.value = settings.bokehScale;
    uniforms.exposure.value = settings.exposure;
    uniforms.aberration.value = settings.aberration;
    uniforms.grain.value = settings.grain;
    uniforms.vignetteAmount.value = settings.vignette;
    uniforms.levels.value = settings.autoLevels;
  }, [settings, uniforms]);

  useEffect(
    () => () => {
      post.dispose();
      levels.dispose();
    },
    [post, levels],
  );

  // Priority 2 leaves room for the display render at priority 1 and stops R3F
  // issuing its own render call.
  useFrame((_state, delta) => {
    // Metering reads the frame the pipeline left in the antialiasing target
    // last time round, so it has to happen before this frame overwrites it.
    levels.measure(renderer, delta);
    post.render();
  }, 2);

  return null;
}
